import { useEffect, useState } from "react";
import { ITournament } from "../../models/ITournament";
import { Baseball, Calendar, CalendarCheck, CreditCard, FileText, Location, Plus, Refresh, SeaWaves, X } from "@mynaui/icons-react";
import { CircleDot, Sun, Target, Volleyball } from "lucide-react";
import { editTournament } from "../../api/tournaments";
import moment from "moment";
import { getTournamentTypes, getSeasonsByType } from "../../api/seasons";
import { ITournamentType } from "../../models/ISeason";
import { ISeason } from "../../models/ISeason";
import { ILocation } from "../../models/ILocation";
import { getAllLocations, createLocation } from "../../api/locations";
import { uploadTournamentCover, base64ToFile } from "../../services/firebase";
import { applyWatermark } from "../../utils/watermark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFutbol } from "@fortawesome/free-solid-svg-icons";
import { Button, DatePicker, Dialog, Field, FileButton, IconButton, Input, Select, Textarea } from "../../ui";
import "./styles.scss";

type TournamentEditDialogProps = {
  item: ITournament;
  isOpen: boolean;
  onClose: (update: boolean) => void;
};

const TournamentEditDialog = (props: TournamentEditDialogProps) => {
  const [name, setName] = useState(props.item.name);
  const [description, setDescription] = useState(props.item.description);
  const [type, setType] = useState(props.item.type);
  const [cover, setCover] = useState(props.item.cover);
  const [locationId, setLocationId] = useState<string | null>(props.item.locationId);
  const [locationObject, setLocationObject] = useState<ILocation | null>(props.item.location);
  const [startDate, setStartDate] = useState(props.item.startDate);
  const [endDate, setEndDate] = useState<string | undefined>(props.item.endDate);
  const [specificDates, setSpecificDates] = useState<Date[]>(
    props.item.specificDates ? props.item.specificDates.split(",").map((d: string) => new Date(d.trim())) : []
  );

  // Normalised to "" so the inputs stay controlled. "" is also the wire value
  // that clears the column: the API reads null as "leave unchanged".
  const [paymentMethodLabel, setPaymentMethodLabel] = useState(props.item.paymentMethodLabel ?? "");
  const [paymentInstructions, setPaymentInstructions] = useState(props.item.paymentInstructions ?? "");

  const [loading, setLoading] = useState(false);
  const [seasonId, setSeasonId] = useState<string | undefined>(props.item.seasonId);
  const [seasonName, setSeasonName] = useState<string | undefined>(undefined);
  const [tournamentTypes, setTournamentTypes] = useState<ITournamentType[]>([]);
  const [seasons, setSeasons] = useState<ISeason[]>([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showNewLocationInput, setShowNewLocationInput] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  const [newLocationLink, setNewLocationLink] = useState("");
  const [creatingLocation, setCreatingLocation] = useState(false);

  const [editDescription, setEditDescription] = useState(false);
  const [editLogistics, setEditLogistics] = useState(false);

  useEffect(() => {
    if (props.isOpen) {
      // Initialize seasonId from props.item
      const initialSeasonId = props.item.seasonId;
      setSeasonId(initialSeasonId);
      setSeasonName(undefined); // Reset season name, will be set when seasons load
      // Initialize location from props.item
      setLocationId(props.item.locationId);
      setLocationObject(props.item.location);
      // This dialog is never unmounted (the parent renders it unconditionally and
      // it returns null when closed), so every field must be re-seeded here or it
      // shows stale values on reopen.
      setPaymentMethodLabel(props.item.paymentMethodLabel ?? "");
      setPaymentInstructions(props.item.paymentInstructions ?? "");
      loadTournamentTypes();
      loadLocations();
    }
  }, [props.isOpen, props.item.seasonId, props.item.locationId, props.item.location, props.item.paymentMethodLabel, props.item.paymentInstructions]);

  const loadTournamentTypes = async () => {
    try {
      const response = await getTournamentTypes();
      setTournamentTypes(response.data);
    } catch (error: any) {
      console.error("Error loading tournament types:", error);
    }
  };

  const loadLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await getAllLocations();
      setLocations(response.data || []);
    } catch (error: any) {
      console.error("Error loading locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleCreateNewLocation = async () => {
    if (!newLocationName.trim()) return;

    setCreatingLocation(true);
    try {
      const response = await createLocation({
        name: newLocationName.trim(),
        googleMapsLink: newLocationLink.trim() || undefined,
      });
      // Add the new location to the list and select it
      setLocations((prev) => [...prev, response.data]);
      setLocationId(response.data.id);
      setLocationObject(response.data);
      // Reset the new location form
      setNewLocationName("");
      setNewLocationLink("");
      setShowNewLocationInput(false);
    } catch (error: any) {
      console.error("Error creating location:", error);
    } finally {
      setCreatingLocation(false);
    }
  };

  useEffect(() => {
    const loadSeasonsForType = async () => {
      if (!type || tournamentTypes.length === 0) {
        setSeasons([]);
        return;
      }

      const selectedType = tournamentTypes.find((t) => t.typeName === type);
      if (!selectedType) {
        setSeasons([]);
        return;
      }

      setLoadingSeasons(true);
      try {
        const response = await getSeasonsByType(selectedType.id);
        setSeasons(response.data);
        // Update season name if seasonId exists - prioritize state seasonId, then props
        const currentSeasonId = seasonId ?? props.item.seasonId;
        if (currentSeasonId) {
          const foundSeason = response.data.find((s) => s.id === currentSeasonId);
          if (foundSeason) {
            setSeasonName(foundSeason.name);
            // Ensure seasonId state is set to the current value
            if (seasonId !== currentSeasonId) {
              setSeasonId(currentSeasonId);
            }
          } else {
            // Season not found in the filtered list - this can happen if the season was from a different type
            // Keep the seasonId if it's from props (original tournament season)
            if (props.item.seasonId === currentSeasonId) {
              // Keep the seasonId even if not in current filtered list
              setSeasonName(undefined);
            } else {
              // Clear if it's not the original tournament season
              setSeasonId(undefined);
              setSeasonName(undefined);
            }
          }
        } else {
          setSeasonName(undefined);
        }
      } catch (error: any) {
        console.error("Error loading seasons:", error);
        setSeasons([]);
      } finally {
        setLoadingSeasons(false);
      }
    };

    loadSeasonsForType();
  }, [type, tournamentTypes, seasonId, props.item.seasonId]);

  const handleClose = () => {
    const isTournamentUpdated =
      name !== props.item.name ||
      description !== props.item.description ||
      type !== props.item.type ||
      cover !== props.item.cover ||
      locationId !== props.item.locationId ||
      startDate !== props.item.startDate ||
      endDate !== props.item.endDate ||
      seasonId !== props.item.seasonId ||
      paymentMethodLabel !== (props.item.paymentMethodLabel ?? "") ||
      paymentInstructions !== (props.item.paymentInstructions ?? "");

    props.onClose(isTournamentUpdated);
  };

  const updateTournament = async (saveType: "description" | "logistics" | "status") => {
    setLoading(true);
    try {
      const originalDates = (props.item.specificDates || "")
        .split(",")
        .filter(Boolean)
        .map((d) => moment(d).format("YYYY-MM-DD"));

      const currentDates = (specificDates || []).map((d) => moment(d).format("YYYY-MM-DD"));
      const datesChanged = JSON.stringify(originalDates) !== JSON.stringify(currentDates);
      const datesForPayload = currentDates.map((d) => new Date(d));

      const res = await editTournament(props.item.id, {
        cover,
        description: props.item.description !== description ? description : undefined,
        locationId: props.item.locationId !== locationId ? locationId : undefined,
        name: props.item.name !== name ? name : undefined,
        startDate: props.item.startDate !== startDate ? startDate : undefined,
        endDate: props.item.endDate !== endDate ? endDate : undefined,
        specificDates: datesChanged ? datesForPayload : undefined,
        type,
        seasonId: props.item.seasonId !== seasonId ? seasonId : undefined,
        // Omit when unchanged; send "" to clear. Never send null — the API reads
        // null as "leave unchanged", so null could never clear the column.
        paymentMethodLabel:
          paymentMethodLabel !== (props.item.paymentMethodLabel ?? "") ? paymentMethodLabel : undefined,
        paymentInstructions:
          paymentInstructions !== (props.item.paymentInstructions ?? "") ? paymentInstructions : undefined,
      });

      setName(res.data.name);
      setDescription(res.data.description);
      setType(res.data.type?.typeName);
      setStartDate(res.data.startDate);
      setEndDate(res.data.endDate);
      setLocationId(res.data.locationId);
      setLocationObject(res.data.location);
      setPaymentMethodLabel(res.data.paymentMethodLabel ?? "");
      setPaymentInstructions(res.data.paymentInstructions ?? "");
      if (res.data.seasonId !== undefined) {
        setSeasonId(res.data.seasonId);
        // Update season name if seasons are loaded
        if (res.data.seasonId) {
          const foundSeason = seasons.find((s) => s.id === res.data.seasonId);
          setSeasonName(foundSeason?.name);
        } else {
          setSeasonName(undefined);
        }
      }
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setLoading(false);
      if (saveType === "description") setEditDescription(false);
      if (saveType === "logistics") setEditLogistics(false);
    }
  };

  const handleFileChange = (files: FileList) => {
    const file = files?.[0];
    if (file && file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      onImageChange(file);
    }
  };

  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        if (!e.target?.result) return reject("Failed to read image.");
        img.src = e.target.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 800;
        const scaleFactor = MAX_WIDTH / img.width;

        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleFactor;

        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("Failed to get canvas context.");

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.7)); // compressed base64
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const onImageChange = async (file: File) => {
    try {
      // Resize the image first
      const base64 = await resizeImage(file);
      // Convert resized base64 back to file for upload
      const resizedFile = await base64ToFile(base64, "tournament-cover.jpg");
      // Apply watermark
      const watermarkedFile = await applyWatermark(resizedFile, name);
      // Upload to Firebase Storage
      const downloadURL = await uploadTournamentCover(props.item.id, watermarkedFile);
      // Update tournament with Firebase URL
      const res = await editTournament(props.item.id, { cover: downloadURL });
      setCover(res.data.cover);
    } catch (error: any) {
      console.error(error.message);
    }
  };



  const sportIcon =
    type === "Beach Volleyball" ? (
      <SeaWaves size={22} />
    ) : type === "Padel" ? (
      <Baseball size={22} />
    ) : type === "Beach Tennis" ? (
      <Sun size={22} />
    ) : type === "Spikeball" ? (
      <CircleDot size={22} />
    ) : type === "Dodgeball" ? (
      <Target size={22} />
    ) : type === "Beach Football" ? (
      <FontAwesomeIcon icon={faFutbol} style={{ fontSize: 20 }} />
    ) : (
      <Volleyball size={22} />
    );

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      placement="side"
      size="sm"
      hideClose
      className="tournament-edit-dialog"
      bodyClassName="tournament-edit-dialog__body"
    >
      <div className="dialog-header">
        {cover && <img src={cover} className="cover" alt="" />}
        <IconButton label="Close" icon={<X size={20} />} className="close-btn" onClick={handleClose} />
        <FileButton
          variant="tint"
          size="sm"
          className="change-cover-btn"
          accept="image/*"
          icon={cover ? <Refresh size={16} /> : <Plus size={16} />}
          onFiles={handleFileChange}
        >
          {cover ? "Change" : "Add"} Cover Photo
        </FileButton>
      </div>

      <div className="dialog-body">
        <div className="details-container">
          {editDescription ? (
            <Field label="Tournament Name">
              {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
            </Field>
          ) : (
            <p className="title">{name}</p>
          )}

          {editDescription ? (
            <Field label="Description">
              {(id) => <Textarea id={id} value={description} onChange={(e) => setDescription(e.target.value)} />}
            </Field>
          ) : (
            <p className="description">{description ?? "-"}</p>
          )}
        </div>

        <div className="dialog-action-buttons">
          {editDescription && (
            <Button variant="ghost" size="sm" onClick={() => setEditDescription(false)}>
              Cancel
            </Button>
          )}
          <Button
            variant={editDescription ? "primary" : "tint"}
            size="sm"
            loading={loading}
            onClick={() => (editDescription ? updateTournament("description") : setEditDescription(true))}
          >
            {editDescription ? "Save" : "Edit Name & Description"}
          </Button>
        </div>

        <div className={`logistics-container ${editLogistics ? "is-editing" : ""}`}>
          <div className="logistic-item">
            {editLogistics ? (
              <Field label="Type">
                {(id) => (
                  <Select
                    id={id}
                    block
                    value={type}
                    onChange={(e) => {
                      setType(e.target.value);
                      setSeasonId(undefined);
                    }}
                  >
                    <option value={"Volleyball"}>Volleyball</option>
                    <option value={"Beach Volleyball"}>Beach Volleyball</option>
                    <option value={"Padel"}>Padel</option>
                    <option value={"Beach Tennis"}>Beach Tennis</option>
                    <option value={"Spikeball"}>Spikeball</option>
                    <option value={"Dodgeball"}>Dodgeball</option>
                    <option value={"Beach Football"}>Beach Football</option>
                  </Select>
                )}
              </Field>
            ) : (
              <>
                <span className="logistic-icon">{sportIcon}</span>
                <p>{type}</p>
              </>
            )}
          </div>

          <div className="logistic-item">
            {editLogistics ? (
              <Field label="Season">
                {(id) => (
                  <Select
                    id={id}
                    block
                    value={seasonId ?? props.item.seasonId ?? ""}
                    onChange={(e) => {
                      const newSeasonId = e.target.value || undefined;
                      setSeasonId(newSeasonId);
                      const selectedSeason = seasons.find((s) => s.id === newSeasonId);
                      setSeasonName(selectedSeason?.name);
                    }}
                    disabled={loadingSeasons || !type}
                  >
                    <option value="">Select a season</option>
                    {seasons.map((season) => (
                      <option key={season.id} value={season.id}>
                        {season.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ) : (
              <>
                <Calendar className="logistic-icon" size={22} />
                <p>{seasonName || "-"}</p>
              </>
            )}
          </div>

          <div className="logistic-item">
            {editLogistics ? (
              <Field label="Start Date">
                {(id) => (
                  <DatePicker
                    id={id}
                    selected={new Date(moment(startDate).format("YYYY-MM-DD"))}
                    onChange={() => undefined}
                    onSelect={(date) => {
                      setStartDate(moment(date).format("YYYY-MM-DD"));
                      setSpecificDates([]);
                      setEndDate(undefined);
                    }}
                    minDate={new Date()}
                  />
                )}
              </Field>
            ) : (
              <>
                <Calendar className="logistic-icon" size={22} />
                <p>{moment(props.item.startDate).format("DD MMM YYYY")}</p>
              </>
            )}
          </div>

          <div className="logistic-item">
            {editLogistics ? (
              <Field label="End Date">
                {(id) => (
                  <DatePicker
                    id={id}
                    selected={endDate ? new Date(moment(endDate).format("YYYY-MM-DD")) : null}
                    onChange={() => undefined}
                    onSelect={(date) => {
                      setEndDate(moment(date).format("YYYY-MM-DD"));
                      setSpecificDates([]);
                    }}
                    minDate={new Date(moment(startDate).format("YYYY-MM-DD"))}
                  />
                )}
              </Field>
            ) : (
              <>
                <CalendarCheck className="logistic-icon" size={22} />
                <p>{moment(endDate).format("DD MMM YYYY")}</p>
              </>
            )}
          </div>

          <div className="logistic-item">
            {editLogistics ? (
              <Field
                label="Specific Dates"
                hint={
                  specificDates.length > 0
                    ? specificDates.map((d) => moment(d).format("DD/MM/YYYY")).join(", ")
                    : undefined
                }
              >
                {(id) => (
                  <DatePicker
                    id={id}
                    selected={null}
                    placeholderText="Pick dates"
                    highlightDates={specificDates}
                    minDate={startDate ? new Date(startDate) : undefined}
                    maxDate={endDate ? new Date(endDate) : undefined}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      const normalized = new Date(date.getFullYear(), date.getMonth(), date.getDate()); // local midnight
                      const exists = specificDates.some(
                        (d) => moment(d).format("YYYY-MM-DD") === moment(normalized).format("YYYY-MM-DD")
                      );
                      const updated = exists
                        ? specificDates.filter(
                            (d) => moment(d).format("YYYY-MM-DD") !== moment(normalized).format("YYYY-MM-DD")
                          )
                        : [...specificDates, normalized];

                      setSpecificDates(updated);
                    }}
                    dayClassName={(date) =>
                      (specificDates || []).some(
                        (d) => moment(d).format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD")
                      )
                        ? "selected-day"
                        : ""
                    }
                  />
                )}
              </Field>
            ) : (
              <>
                <Calendar className="logistic-icon" size={22} />
                <p>
                  {specificDates && specificDates.length > 0
                    ? specificDates.map((d) => moment(d).format("DD MMM YYYY")).join(", ")
                    : "-"}
                </p>
              </>
            )}
          </div>

          <div className="logistic-item">
            {editLogistics ? (
              <Field label="Location">
                {(id) =>
                  showNewLocationInput ? (
                    <div className="new-location-form">
                      <Input
                        id={id}
                        placeholder="Location name"
                        value={newLocationName}
                        onChange={(e) => setNewLocationName(e.target.value)}
                        disabled={creatingLocation}
                      />
                      <Input
                        placeholder="Google Maps link (optional)"
                        value={newLocationLink}
                        onChange={(e) => setNewLocationLink(e.target.value)}
                        disabled={creatingLocation}
                      />
                      <div className="new-location-actions">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowNewLocationInput(false);
                            setNewLocationName("");
                            setNewLocationLink("");
                          }}
                          disabled={creatingLocation}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCreateNewLocation}
                          disabled={creatingLocation || !newLocationName.trim()}
                          loading={creatingLocation}
                        >
                          Create
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="location-picker">
                      <Select
                        id={id}
                        block
                        value={locationId || ""}
                        onChange={(e) => {
                          const newLocationId = e.target.value || null;
                          setLocationId(newLocationId);
                          const selectedLoc = locations.find((l) => l.id === newLocationId);
                          setLocationObject(selectedLoc || null);
                        }}
                        disabled={loadingLocations}
                      >
                        <option value="">Select a location</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </Select>
                      <Button variant="link" size="sm" icon={<Plus size={16} />} onClick={() => setShowNewLocationInput(true)}>
                        Add new location
                      </Button>
                    </div>
                  )
                }
              </Field>
            ) : (
              <>
                <Location className="logistic-icon" size={22} />
                {locationObject ? (
                  locationObject.googleMapsLink ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="link"
                      onClick={() => window.open(locationObject.googleMapsLink!, "_blank")}
                    >
                      {locationObject.name}
                    </Button>
                  ) : (
                    <p>{locationObject.name}</p>
                  )
                ) : (
                  <p className="not-set">Not set</p>
                )}
              </>
            )}
          </div>

          <div className="logistic-item payment-item">
            {editLogistics ? (
              <Field
                label="Payment method label"
                hint={
                  <>
                    Shown to players as the name of the transfer payment option. Leave empty to show &ldquo;InstaPay&rdquo;.
                  </>
                }
              >
                {(id) => (
                  <Input
                    id={id}
                    value={paymentMethodLabel}
                    maxLength={100}
                    placeholder="InstaPay"
                    onChange={(e) => setPaymentMethodLabel(e.target.value)}
                  />
                )}
              </Field>
            ) : (
              <>
                <CreditCard className="logistic-icon" size={22} />
                <p>{props.item.paymentMethodLabel || "InstaPay"}</p>
              </>
            )}
          </div>

          <div className="logistic-item payment-item">
            {editLogistics ? (
              <Field
                label="Payment instructions"
                hint="Shown to players when they pick this payment method, and in registration emails. Links and phone numbers become tappable. Leave empty to show the default InstaPay link."
              >
                {(id) => (
                  <Textarea
                    id={id}
                    value={paymentInstructions}
                    rows={8}
                    placeholder={"Bank transfer details, payment link, phone number…"}
                    onChange={(e) => setPaymentInstructions(e.target.value)}
                  />
                )}
              </Field>
            ) : (
              <>
                <FileText className="logistic-icon" size={22} />
                {props.item.paymentInstructions ? (
                  <p className="payment-instructions-preview">{props.item.paymentInstructions}</p>
                ) : (
                  <p className="not-set">Not set</p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="dialog-action-buttons">
          {editLogistics && (
            <Button variant="ghost" size="sm" onClick={() => setEditLogistics(false)}>
              Cancel
            </Button>
          )}
          <Button
            variant={editLogistics ? "primary" : "tint"}
            size="sm"
            loading={loading}
            onClick={() => (editLogistics ? updateTournament("logistics") : setEditLogistics(true))}
          >
            {editLogistics ? "Save" : "Edit Logistics"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default TournamentEditDialog;
